import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable } from 'rxjs';
import { ApiService } from './api.service';
import { DigiResponse } from '@core/models/response/core';
import { PupilLinkMismatch } from '@core/models/domain/pupil-link.domain';
import {
  CreatePupilForPupilLinkMismatchRequest,
  DismissPupilLinkMismatchRequest,
  ResolvePupilLinkMismatchRequest
} from '@core/models/request/pupil-link.request';
import { buildApiEndpoint, handleError } from '@core/utils/service';

/**
 * PupilLinkService
 *
 * Backend calls for the school admin "Pupil link requests" page: parent requests to link a child
 * that matched several pupils, only similar pupils, or a pupil already in another family, and need
 * an admin decision (link a pupil, create a new one, or reject). The school is taken from the
 * caller's token by the backend, never sent from here.
 */
@Injectable({
  providedIn: 'root'
})
export class PupilLinkService {
  API_URI: string;

  constructor(private http: HttpClient, private apiService: ApiService) {
    this.API_URI = this.apiService.getApiURL("UserService", "school-account");
  }

  loadMismatches(): Observable<PupilLinkMismatch[]> {
    return this.http
      .get<DigiResponse<PupilLinkMismatch[]>>(buildApiEndpoint(this.API_URI, "pupil-link-mismatches"))
      .pipe(
        map((response) => {
          if (response?.statusCode?.status !== "OK") {
            throw response?.statusCode ?? new Error("Loading pupil link requests failed");
          }
          return Array.isArray(response.body) ? response.body : [];
        }),
        catchError((error) => handleError<PupilLinkMismatch[]>("Loading pupil link requests", error))
      );
  }

  resolve(request: ResolvePupilLinkMismatchRequest): Observable<string> {
    return this.http
      .post<DigiResponse<string>>(buildApiEndpoint(this.API_URI, "pupil-link-mismatches/resolve"), request)
      .pipe(
        map((response) => {
          if (response?.statusCode?.status !== "OK") {
            throw response?.statusCode ?? new Error("Linking the pupil failed");
          }
          return request.mismatchId;
        }),
        catchError((error) => handleError<string>("Linking pupil", error))
      );
  }

  createPupil(request: CreatePupilForPupilLinkMismatchRequest): Observable<string> {
    return this.http
      .post<DigiResponse<string>>(buildApiEndpoint(this.API_URI, "pupil-link-mismatches/create-pupil"), request)
      .pipe(
        map((response) => {
          if (response?.statusCode?.status !== "OK") {
            throw response?.statusCode ?? new Error("Creating the pupil failed");
          }
          return request.mismatchId;
        }),
        catchError((error) => handleError<string>("Creating pupil", error))
      );
  }

  dismiss(request: DismissPupilLinkMismatchRequest): Observable<string> {
    return this.http
      .post<DigiResponse<string>>(buildApiEndpoint(this.API_URI, "pupil-link-mismatches/dismiss"), request)
      .pipe(
        map((response) => {
          if (response?.statusCode?.status !== "OK") {
            throw response?.statusCode ?? new Error("Dismissing the request failed");
          }
          return request.mismatchId;
        }),
        catchError((error) => handleError<string>("Dismissing pupil link request", error))
      );
  }
}
